import { Box, type BoxProps } from "@mui/material";
import { type ReactNode } from "react";

export interface TwoColumnGridProps {
  /**
   * The items to render in the grid
   */
  children: ReactNode[];
  /**
   * Additional props to pass to the container
   */
  sx?: BoxProps["sx"];
  /**
   * Gap between items (default: 1.5)
   */
  gap?: number | { sm: number; xs: number };
  /**
   * Whether to show single column layout (e.g. on mobile)
   */
  singleColumn?: boolean;
}

/**
 * A responsive two-column grid that maintains item order while filling space efficiently
 */
export const TwoColumnGrid = ({ children, sx, gap = { sm: 1.5, xs: 1 }, singleColumn = false }: TwoColumnGridProps) => {
  if (singleColumn) {
    return (
      <Box data-testid="two-column-grid" sx={{ display: "flex", flexDirection: "column", gap, width: "100%", ...sx }}>
        {children}
      </Box>
    );
  }

  // Split items into two columns while maintaining order
  const leftColumn: ReactNode[] = [];
  const rightColumn: ReactNode[] = [];

  children.forEach((child, index) => {
    if (index % 2 === 0) {
      leftColumn.push(child);
    } else {
      rightColumn.push(child);
    }
  });

  return (
    <Box
      data-testid="two-column-grid"
      sx={{
        display: "grid",
        gap,
        gridTemplateColumns: "1fr 1fr",
        width: "100%",
        ...sx,
      }}
    >
      <Box data-testid="left-column" sx={{ display: "flex", flexDirection: "column", gap }}>
        {leftColumn}
      </Box>
      <Box data-testid="right-column" sx={{ display: "flex", flexDirection: "column", gap }}>
        {rightColumn}
      </Box>
    </Box>
  );
};
